/**
 * Tool cards metadata for the marketing tools grid and sitemap.
 * `/tools` grid: sorted via `sortMarketingTools()` (type, then weight ascending).
 * Icons: `routes/_marketing/tools/index.tsx` (`TOOL_ICONS` keyed by title).
 */
export type MarketingToolDefinition = {
	title: string;
	description: string;
	href: string;
	beta?: boolean;
	iconColor?: string;
	type: ToolType;
	weight: number;
};

export type ToolType = "bulk" | "single";

/** `single` before `bulk`; then ascending `weight`. */
const TOOL_TYPE_SORT_ORDER: Record<ToolType, number> = {
	single: 0,
	bulk: 1,
};

export function compareMarketingTools(
	a: MarketingToolDefinition,
	b: MarketingToolDefinition,
): number {
	const byType = TOOL_TYPE_SORT_ORDER[a.type] - TOOL_TYPE_SORT_ORDER[b.type];
	if (byType !== 0) return byType;
	return a.weight - b.weight;
}

export function sortMarketingTools(
	definitions: readonly MarketingToolDefinition[],
): MarketingToolDefinition[] {
	return [...definitions].sort(compareMarketingTools);
}

export function partitionMarketingToolsByType(
	definitions: readonly MarketingToolDefinition[],
): {
	single: MarketingToolDefinition[];
	bulk: MarketingToolDefinition[];
} {
	const sorted = sortMarketingTools(definitions);
	return {
		single: sorted.filter((def) => def.type === "single"),
		bulk: sorted.filter((def) => def.type === "bulk"),
	};
}

export const MARKETING_TOOL_DEFINITIONS: readonly MarketingToolDefinition[] = [
	{
		title: "Color Palette Generator",
		description:
			"Generate cohesive palettes with accessible contrast checks before you commit to a brand direction.",
		href: "#",
		type: "single",
		weight: 1,
	},
	{
		title: "JSON Formatter",
		description:
			"Validate, prettify, and minify JSON payloads while you debug integrations and webhooks.",
		href: "#",
		type: "single",
		weight: 2,
	},
	{
		title: "CSS Flexbox Generator",
		description:
			"Prototype flex layouts visually, then copy production-ready declarations into your codebase.",
		href: "#",
		type: "single",
		weight: 3,
	},
	{
		title: "SVG Editor",
		description:
			"Adjust paths and basic shapes in the browser when you need a lightweight vector tweak.",
		href: "#",
		type: "single",
		weight: 4,
	},
	{
		title: "Markdown Preview",
		description:
			"Preview GitHub-flavored Markdown with styling that mirrors how readers will see it live.",
		href: "#",
		type: "single",
		beta: true,
		weight: 10,
	},
	{
		title: "Image Optimizer",
		description:
			"Shrink PNG, JPG, and WebP assets for faster marketing pages without visible quality loss.",
		href: "#",
		type: "bulk",
		weight: 15,
	},
];
